import React, { useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';

import Header from './components/Header';
import FloatingActions from './components/FloatingActions';

import Home from './pages/Home';
import NotFound from './pages/NotFound';

const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const Projects = lazy(() => import('./pages/Projects'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Contact = lazy(() => import('./pages/Contact'));
const FreelancerInVirar = lazy(() => import('./pages/FreelancerInVirar'));
const WebsiteDesignerVirarWest = lazy(() =>
  import('./pages/WebsiteDesignerVirarWest')
);
const LocationLandingPage = lazy(() => import('./pages/LocationLandingPage'));
const KeywordLandingPage = lazy(() => import('./pages/KeywordLandingPage'));

import './pages/Home.css';

function PageLoader() {
  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span style={{ color: '#ff184e', fontWeight: 700 }}>Loading…</span>
    </div>
  );
}

const locationSlugs = [
  'web-developer-dahanu-road',
  'web-developer-vangaon',
  'website-designer-boisar',
  'web-developer-umroli',
  'website-development-palghar',
  'web-developer-kelve-road',
  'website-designer-saphale',
  'web-developer-vaitarna',
  'web-developer-virar',
  'web-developer-virar-west',
  'website-designer-nallasopara',
  'web-developer-vasai-road',
  'web-developer-vasai',
  'web-developer-naigaon',
  'website-development-bhayandar',
  'website-development-mira-road',
  'web-developer-dahisar',
];

const keywordSlugs = [
  'corporate-website-development',
  'building-website-for-business',
  'building-website',
  'business-website-design',
  'website-development',
  'website-developer-near-me',
  'freelance-web-designers-in-virar',
  'wordpress-website-development-mumbai',
  'website-developer',
  'website-development-freelancer',
  'freelance-website-designer',
  'web-developer-in-mumbai',
  'website-developers',
  'freelance-website-designer-in-mumbai',
  'freelance-web-developer-in-mumbai',
  'website-designers-in-mumbai',
];

const blogSlugs = [
  'why-choose-a-freelance-website-developer-in-mumbai',
  'popular-internet-website-developers-in-virar-west',
  'top-freelancers-for-internet-website-designers-in-mumbai',
  'freelance-service-for-website-design-and-development',
  'web-designers-in-virar-mumbai-india',
  'professional-web-designer-website-developer-in-vasai-virar',
  'virar-vasai-web-designing-website-vasai-virar-design-website-development',
  'digital-marketing-website-designing-near-virar-west-vasai',
  'best-static-website-development-services-company-in-virar',
  'website-design-and-development-virar',
  'website-development-in-vasai-virar',
];

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  const hideHeaderOnHome = location.pathname === '/';

  return (
    <>
      <ScrollToTop />

      {!hideHeaderOnHome && <Header />}

      <main>
        <AnimatePresence mode="wait">
          <Suspense fallback={<PageLoader />}>
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home />} />

              <Route path="/about" element={<About />} />
              <Route path="/about-us" element={<Navigate to="/about" replace />} />

              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/projects" element={<Projects />} />

              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blogs/:slug" element={<BlogPost />} />

              <Route
                path="/freelancer-in-virar"
                element={<FreelancerInVirar />}
              />

              <Route
                path="/website-designer-virar-west"
                element={<WebsiteDesignerVirarWest />}
              />

              {keywordSlugs.map((slug) => (
                <Route
                  key={slug}
                  path={`/${slug}`}
                  element={<KeywordLandingPage slug={slug} />}
                />
              ))}

              {locationSlugs.map((slug) => (
                <Route
                  key={slug}
                  path={`/${slug}`}
                  element={<LocationLandingPage slug={slug} />}
                />
              ))}

              <Route path="/contact" element={<Contact />} />
              <Route path="/contact-us" element={<Navigate to="/contact" replace />} />

              {blogSlugs.map((slug) => (
                <Route
                  key={slug}
                  path={`/${slug}`}
                  element={<BlogPost slug={slug} />}
                />
              ))}

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AnimatePresence>
      </main>

      <FloatingActions />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </HelmetProvider>
  );
}
